import { css } from "@emotion/react";
import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AiSummary, Archives, Automation, ChevronLeft, ChevronRight, Delete } from "@livechat/design-system-icons";
import {
  Button,
  Icon as DSIcon,
  DesignToken,
  IconSource,
  Popover,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import { Icon, IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import ShortcutTooltip from "components/shortcut-tooltip/ShortcutTooltip";
import { KeyModifier, Shortcuts } from "components/shortcuts/Shortcuts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { EventType, ITicket, TicketSilo } from "interfaces/entities/ticket";
import { MacroPicker } from "routes/automations/macros/components/MacroPicker";
import { selectIsAgent, selectIsViewer } from "store/entities/agents";
import { selectTicketSummary } from "store/entities/license";
import { TicketActions } from "store/entities/tickets/actions";
import { IWithTicketsViewState, selectIsSummarizingTicketById } from "store/views/tickets/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import TicketToolbarLabels from "./TicketToolbarLabels";

interface IToolbarButtonProps {
  iconSource?: IconSource;
  customIcon?: React.ReactElement;
  action?: () => void;
  tooltipContent?: string;
  buttonDisabled?: boolean;
  tooltipPlacement?:
    | "bottom"
    | "top"
    | "right"
    | "left"
    | "bottom-start"
    | "bottom-end"
    | "top-start"
    | "top-end"
    | "right-start"
    | "right-end"
    | "left-start"
    | "left-end";
}

const ToolbarButton: FC<IToolbarButtonProps> = ({
  iconSource,
  action,
  customIcon,
  tooltipContent,
  buttonDisabled,
  tooltipPlacement,
}) => (
  <Tooltip
    kind="invert"
    isVisible={buttonDisabled ? false : undefined}
    placement={tooltipPlacement || "bottom"}
    triggerRenderer={() => (
      <Button
        data-testid={`ticket-toolbar-${tooltipContent
          ?.toLowerCase()
          .trim()
          .replace(/[^a-z0-9]/g, "-")}-button`}
        kind="plain"
        size="compact"
        icon={customIcon || (iconSource && <DSIcon source={iconSource} />)}
        onClick={action}
        disabled={buttonDisabled}
      />
    )}
  >
    <Text>{tooltipContent}</Text>
  </Tooltip>
);

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function Separator() {
  return (
    <div
      css={css`
        width: 1px;
        background-color: ${withVar(DesignToken.BorderBasicSecondary)};
      `}
    />
  );
}

export interface IProps {
  shortcutsConfig: {
    [key: number]: {
      eventHandler: () => void;
      additionalKeysRequired: KeyModifier[];
      disableDefaultBehavior?: boolean;
    };
  };
  ticket: ITicket;
  shouldSeeNavigationInTicketTooltip: boolean;
  handleFetchPreviousTicket: () => void;
  handleFetchNextTicket: () => void;
  isPreviousTicketLoading: boolean;
  isNextTicketLoading: boolean;
  isMacUser: boolean;
  entryLastMessageAt: string;
  canManageTickets: boolean;
  isMemberOfTicketTeam: boolean;
  isMobile: boolean;
  handleSiloChange: (silo: TicketSilo) => void;
  onDeleteTicket: () => void;
}

export const TicketToolbar: FC<IProps> = ({
  shortcutsConfig,
  shouldSeeNavigationInTicketTooltip,
  handleFetchNextTicket,
  handleFetchPreviousTicket,
  isMacUser,
  isPreviousTicketLoading,
  isNextTicketLoading,
  entryLastMessageAt,
  ticket,
  canManageTickets,
  isMemberOfTicketTeam,
  handleSiloChange,
  isMobile,
  onDeleteTicket,
}) => {
  const dispatch = useDispatch();
  const { id: ticketId, events: ticketEvents, silo: ticketSilo } = ticket || {};
  const isViewer = useSelector(selectIsViewer);
  const isAgent = useSelector(selectIsAgent);
  const showTicketSummary = useSelector(selectTicketSummary);
  const isSummarizingCurrentTicket = useSelector(state =>
    selectIsSummarizingTicketById(state as IWithTicketsViewState, ticketId)
  );
  const isRecentTicket = ticketSilo === TicketSilo.Tickets;
  const displayToolbarLabels =
    !isRecentTicket || ticket?.parentTicket !== null || (!isMemberOfTicketTeam && canManageTickets);
  const shouldAllowToSummarize = useMemo(() => {
    if (ticketEvents) {
      for (let i = ticketEvents.length - 1; i >= 0; i -= 1) {
        const event = ticketEvents[i];
        if (event.type === EventType.Message) {
          return true;
        }
        if (event.type === EventType.Summary) {
          return false;
        }
      }
    }
    return false;
  }, [ticketEvents]);

  const [macroPickerOpened, setMacroPickerOpened] = useState(false);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onMacroButtonClick = () => {
    setMacroPickerOpened(true);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSummarizeTicket = () => {
    dispatch(TicketActions.summarizeTicket({ ticketId }));
    trackEngagement("Ticket feed toolbar: AI Ticket summary", EventPlace.Tickets, {});
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onSiloChange = (silo: TicketSilo) => {
    trackEngagement("Tickets: Change silo actions", EventPlace.TicketToolbar, {
      from: ticketSilo,
      to: silo,
      place: "Ticket feed toolbar",
    });
    handleSiloChange(silo);
  };

  return (
    <Flex
      data-testid="ticket-toolbar"
      padding={withVar(SpacingToken.Spacing3)}
      alignItems="center"
      justifyContent="space-between"
      borderBottom={`1px solid ${withVar(DesignToken.BorderBasicSecondary)}`}
    >
      <Flex gap="8px">
        {displayToolbarLabels && ticket ? (
          <TicketToolbarLabels
            ticket={ticket}
            isViewer={isViewer}
            isAgent={isAgent}
            canManageTickets={canManageTickets}
            isMemberOfTicketTeam={isMemberOfTicketTeam}
            onSiloChange={onSiloChange}
            onDeleteTicket={onDeleteTicket}
          />
        ) : (
          <>
            <Tooltip
              placement="bottom-start"
              kind="invert"
              triggerRenderer={() => (
                <Popover
                  placement="bottom-start"
                  triggerRenderer={() => (
                    <Button
                      kind="plain"
                      size="compact"
                      icon={<Automation />}
                      onClick={onMacroButtonClick}
                      disabled={isViewer}
                    />
                  )}
                  isVisible={macroPickerOpened}
                  onClose={() => setMacroPickerOpened(false)}
                  css={{ zIndex: StackingContextLevel.FullOverlay }}
                >
                  <MacroPicker ticket={ticket} onClose={() => setMacroPickerOpened(false)} />
                </Popover>
              )}
            >
              Macros
            </Tooltip>
            {!isMobile && <Separator />}
            {showTicketSummary && (
              <>
                <Tooltip
                  isVisible={isViewer ? false : undefined}
                  placement="bottom-start"
                  triggerRenderer={() => (
                    <Button
                      data-testid="ticket-toolbar-summary-button"
                      kind="plain"
                      size="compact"
                      onClick={handleSummarizeTicket}
                      disabled={isSummarizingCurrentTicket || !shouldAllowToSummarize || !isRecentTicket || isViewer}
                      loading={isSummarizingCurrentTicket}
                      css={css({ position: "relative" })}
                      icon={<DSIcon source={AiSummary} />}
                    />
                  )}
                  kind="invert"
                >
                  <Text textAlign="center">
                    {shouldAllowToSummarize
                      ? "Summarize this ticket as a private note"
                      : "There's no new content to summarize in this ticket"}
                  </Text>
                </Tooltip>
                <Separator />
              </>
            )}
            <ToolbarButton
              iconSource={Archives}
              action={() => onSiloChange(TicketSilo.Archive)}
              tooltipContent="Move to Archive"
              buttonDisabled={!canManageTickets || !isRecentTicket}
              tooltipPlacement={showTicketSummary ? "bottom" : "bottom-start"}
            />
            <ToolbarButton
              customIcon={<Icon name={IconsEnum.SpamTabler} width="20px" height="20px" />}
              action={() => onSiloChange(TicketSilo.Spam)}
              tooltipContent="Move to Spam"
              buttonDisabled={!canManageTickets || !isRecentTicket}
            />
            <ToolbarButton
              iconSource={Delete}
              action={() => onSiloChange(TicketSilo.Trash)}
              tooltipContent="Move to Trash"
              buttonDisabled={!canManageTickets || !isRecentTicket}
            />
          </>
        )}
      </Flex>
      <Flex alignSelf="flex-start">
        <Shortcuts config={shortcutsConfig}>
          <Flex gap="8px" alignItems="center">
            <ShortcutTooltip
              trigger={
                <Button
                  icon={<ChevronLeft />}
                  kind="plain"
                  size="compact"
                  disabled={!entryLastMessageAt}
                  loading={isPreviousTicketLoading}
                  onClick={() => {
                    handleFetchPreviousTicket();
                    trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
                      moveTo: "previous",
                      method: "arrow",
                    });
                  }}
                />
              }
              hideTooltip={shouldSeeNavigationInTicketTooltip}
              title="Previous ticket"
              firstKey={isMacUser ? "⌃" : "ctrl"}
              secondKey={isMacUser ? "⌥" : "alt"}
              thirdKey="J"
            />
            <ShortcutTooltip
              trigger={
                <Button
                  icon={<ChevronRight />}
                  kind="plain"
                  size="compact"
                  disabled={!entryLastMessageAt}
                  loading={isNextTicketLoading}
                  onClick={() => {
                    handleFetchNextTicket();
                    trackEngagement("Ticket details: Navigation", EventPlace.Tickets, {
                      moveTo: "next",
                      method: "arrow",
                    });
                  }}
                />
              }
              hideTooltip={shouldSeeNavigationInTicketTooltip}
              title="Next ticket"
              firstKey={isMacUser ? "⌃" : "ctrl"}
              secondKey={isMacUser ? "⌥" : "alt"}
              thirdKey="K"
            />
          </Flex>
        </Shortcuts>
      </Flex>
    </Flex>
  );
};
